/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: dashboard-core (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./SaveActionHandler',
	'../../../dashboard/glass/util/SaveActionUtil',
	'../../../app/util/ErrorUtils',
	'../../../app/util/GlassUtil',
	'../../../lib/@waca/dashboard-common/dist/utils/URLUtil'
], function (BaseClass, SaveActionUtil, ErrorUtils, GlassUtil, URLUtil) {

	return BaseClass.extend({

		onSelectItem: function (context) {
			const contentView = this.getContentView(context);
			const id = contentView.getBoardId();
			this.saveAsTemplate(context, id);
		},

		isItemVisible: function (context) {
			const isDashboardTemplatesEnabled = !context.glassContext.getCoreSvc('.FeatureChecker').checkValue('dashboard', 'dashboardTemplates', 'disabled');
			return isDashboardTemplatesEnabled && this._isBoardTypeSupported(context) && ErrorUtils.hasCapability(context.glassContext, 'canAuthorDashboard');
		},

		_isBoardTypeSupported: function (context) {
			const view = this.getContentView(context);
			return view.getDashboardApi().getType() === 'dashboard';
		},

		/**
		 * Prompt the user for a location to save the dashboard template.
		 * @param {Object} context of selection (passed during onSelect)
		 * @param {string} id The Board Id that we are creaing a template from
		 */
		saveAsTemplate: function (context, id) {
			const dashboardView = this.getDashboardView(context);
			return this._getAncestors(context, id).then( ancestors => {
				return context.glassContext.getSvc('.ContentDialogFactory').then(dialogFactory => {
					const stringResources = this.getStringResourcesService(context);
					this._saveAsTemplateDialog = dialogFactory.createSaveAsDialog({
						glassContext: context.glassContext,
						defaultFileName: stringResources.get('dashboard_template_prefix', { name: dashboardView.boardModel.name }),
						service: {
							context: context,
							save: this.onSaveAsTemplate.bind(this)
						},
						ancestors: ancestors
					});
					this._saveAsTemplateDialog.open();
				});
			});
		},

		onSaveAsTemplate: function (service, selection, name, overwrite) {
			return this._executeSaveAsTemplateRequest(service.context, selection.url, name, overwrite)
				.then(this._onSaveAsTemplateSuccess.bind(this), this._onSaveAsError.bind(this, service.context))
				.finally(this._saveAsTemplateDialog.hide.bind(this._saveAsTemplateDialog));
		},

		_onSaveAsTemplateSuccess: function (params) {
			const context = params.context;
			const glassContext = context.glassContext;
			return glassContext.appController.openAppView('dashboardTemplate', {
				perspective: 'dashboardTemplate',
				content: {
					perspective: 'dashboardTemplate',
					boardId: URLUtil.getBoardIdFromUrl(params.saveRequest.getResponseHeader('location')),
					isAuthoringMode: glassContext.appController.getCurrentContentView().isAuthoringMode
				}
			}).then(() => {
				const stringResources = this.getStringResourcesService(context);
				const contentView = this.getContentView(context);
				const id = contentView.getBoardId();

				// Remove any filter widgets - ideally, this should be done before the template spec is saved to CM,
				// but there's no dashboard api until the view is opened, once this is available - update this
				const canvas = contentView.getDashboardApi().getCanvas();
				const filterWidgets = canvas.findContent({ type: 'widget' }).filter(widget => widget.getType() === 'widget.filter');
				if (filterWidgets.length) {
					filterWidgets.forEach(widget => {
						canvas.removeContent(widget.getId());
					});
					glassContext.appController.showToast(stringResources.get('dashboard_template_filters_removed'), { type: 'warning' });
					this.save(context, true, false);
				}
				// Ensure widgets of current tab have rendered before taking screen shot
				const widgets = canvas.getContent(contentView.boardModel.getSelectedLayout()).findContent({ type: 'widget' });
				const widgetStatePromises = widgets.map(widget => {
					const state = widget.getFeature('state');
					return state.whenStatusChanges(state.STATUS.RENDERED);
				});
				return Promise.all(widgetStatePromises).then(() => {
					return this._saveThumbnail(context, id).catch(err => {
						glassContext.getCoreSvc('.Logger').error(err);
					});
				}).then(() => {
					glassContext.appController.showToast(stringResources.get('dashboard_template_save_success'));
				});
			});
		},

		/**
		 * Send a create request to create a new dashboard template in CM
		 * @return {Promise} Resolve when save succeeds; reject when save fails
		 */
		_executeSaveAsTemplateRequest: function (context, url, name, overwrite) {
			const { glassContext } = context;
			const ajaxSvc = GlassUtil.getAjaxService(glassContext);
			const dashboardView = this.getDashboardView(context);
			const dashboard = dashboardView.getDashboardApi();
			const convertToTemplate = dashboard.getFeature('ConvertToTemplate');

			const spec = dashboard.getFeature('Serializer').toJSON();
			SaveActionUtil.cleanUnusedCustomColors(dashboard, spec, dashboardView.boardModel);
			if (name) {
				spec.name = name;
			}

			return new Promise((resolve, reject) => {
				convertToTemplate.convertToTemplate(spec).then((templateSpec) => {
					const cmSpec = this._getCMSpec(templateSpec, context);
					url = url + (overwrite ? '?updateAction=replace&ignoreInvalidObjectReference=true' : '?ignoreInvalidObjectReference=true');
					ajaxSvc.post(url, {
						contentType: 'application/json',
						processData: false,
						dataType: 'text',
						data: JSON.stringify(cmSpec)
					}).done((data, status, response) => {
						resolve({
							'saveRequest': response,
							'context': context
						});
					}).fail((ajaxRequestDeferred, response) => {
						reject({
							'saveRequest': response,
							'cmSpec': spec,
							'glassContext': glassContext
						});
					});
				});
			});
		},

		_getCMSpec(spec, context) {
			const templateSpec = Object.assign(spec, { dataSources: {} });
			const contentView = this.getContentView(context);
			const dashboard = contentView.getDashboardApi();
			return {
				defaultName: templateSpec.name,
				type: SaveActionUtil.getAssetType(dashboard),
				specification: JSON.stringify(templateSpec),
				tags: [
					'dashboard_template'
				]
			};
		}
	});

});
